<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FieldsetPaymentGeneral',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits([
    'reload',
    'close',
    'save',
    'accountChanged',
    'autocalcAmountChanged',
  ]);

  const props = defineProps({
    ...userComponentProps,
    payment: {
      type: Object,
      required: true,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
    config: {
      type: Object,
      required: true,
    },
    transactionTypes: {
      type: Object,
      required: false,
    },
    autocalcAmount: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const isFieldEnabled = (...args) => {
    const name = args[0];
    return (
      !props.config ||
      !props.config.fields ||
      props.config.fields[name] != false
    );
  };
  const isFieldReadonly = (...args) => {
    const name = args[0];
    return (
      isReadOnly.value ||
      (props.config && props.config.fields && props.config.fields[name] == 'ro')
    );
  };
  const loadEmployees = (...args) => {
    return $fn.value
      .fetch('list_company_employees', {
        parameters: { company_id: props.payment.account.owner.id },
      })
      .then((items) => items.sort((a, b) => a.name.localeCompare(b.name)));
  };

  const autocalcAmountInternal = ref(null);
  const transactionTitles = ref(null);
  const paymentMethods = ref(null);
  const employees = ref(null);
  const ux = ref([]);
  const b_8697745105058001_dataSource = computed(() => ({
    name: 'suggest_company',
    parameters: {
      role: 'partner',
    },
  }));

  const $data = computed(() => ({
    autocalcAmountInternal: autocalcAmountInternal.value,
    transactionTitles: transactionTitles.value,
    paymentMethods: paymentMethods.value,
    employees: employees.value,
    ux: ux.value,
    b_8697745105058001_dataSource: b_8697745105058001_dataSource.value,
  }));

  const $$load_autocalcAmountInternal = () => {
    return props.autocalcAmount;
  };
  const $$load_transactionTitles = () => {
    return $fn.value
      .fetch('get_tags', { parameters: { tag_type: 'payment' } })
      .then((data) =>
        _.map(data, (p) => p.name).sort((a, b) => a.localeCompare(b))
      );
  };
  const $$load_paymentMethods = () => {
    return $fn.value.fetch('list_payment_method').then((data) =>
      _.chain(data)
        .filter((p) =>
          props.payment.account.account_type !== 'cash_book' ? !p.cash : p.cash
        )
        .map((p) => ({
          value: p.id,
          text: $fn.value.pgettext('PaymentMethod', p.name),
        }))
        .value()
    );
  };
  const $$load_employees = () => {
    return loadEmployees();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'autocalcAmountInternal' },
      { name: 'transactionTitles' },
      { name: 'paymentMethods' },
      { name: 'employees' },
      { name: 'ux' },
      { name: 'b_8697745105058001_dataSource', nodeId: '8697745105058001' },
    ],
    loaders: {
      autocalcAmountInternal: $$load_autocalcAmountInternal,
      transactionTitles: $$load_transactionTitles,
      paymentMethods: $$load_paymentMethods,
      employees: $$load_employees,
    },
    ux: {},
    dataRefs: {
      autocalcAmountInternal,
      transactionTitles,
      paymentMethods,
      employees,
      ux,
      b_8697745105058001_dataSource,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const isReadOnly = computed(() => {
    return props.payment.isReadonly;
  });
  const cashBookPayment = computed(() => {
    return (
      props.payment.account && props.payment.account.account_type == 'cash_book'
    );
  });

  watch(
    () => props.payment,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('payment');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );
  watch(
    () => props.config,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('config');
    }
  );
  watch(
    () => props.transactionTypes,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('transactionTypes');
    }
  );
  watch(
    () => props.autocalcAmount,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('autocalcAmount');
    }
  );

  const act_4615685607825208_setData_41hmdzw7z_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'payment.currency',
      value: $event.data.currency,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_4615685607825208_script_kzeh82gxn_value_callable = async (
    $event
  ) => {
    return loadEmployees().then((items) => setData('employees', items));
  };
  const act_4615685607825208_script_kzeh82gxn_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_4615685607825208_script_kzeh82gxn_value_callable($event);
      },
    };
  };
  const act_4615685607825208_emit_ksk21x00v_getActionArgs = async ($event) => {
    return {
      event: 'accountChanged',
      value: $event.data,
    };
  };
  const act_47936024368137_emit_sww6hcxgj_getActionArgs = async ($event) => {
    return {
      event: 'autocalcAmountChanged',
      value: autocalcAmountInternal.value,
    };
  };

  const act_7315092382398562_reloadComponentData_brbrimkjm =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_3dueb5g2j =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_brbrimkjm,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_3dueb5g2j],
    event: 'reload',
  });
  const act_4615685607825208_setData_41hmdzw7z = new actionTypes.SetDataAction({
    actionArgs: act_4615685607825208_setData_41hmdzw7z_getActionArgs,
  });
  const inst_evh_4615685607825208_updateModelValue_tjuos7a6q =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_setData_41hmdzw7z,
      event: 'update:modelValue',
      displayName: 'setData',
    });
  const act_4615685607825208_script_kzeh82gxn = new actionTypes.ScriptAction({
    actionArgs: act_4615685607825208_script_kzeh82gxn_getActionArgs,
  });
  const inst_evh_4615685607825208_updateModelValue_mq752jdse =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_script_kzeh82gxn,
      event: 'update:modelValue',
      displayName: 'script',
    });
  const act_4615685607825208_emit_ksk21x00v = new actionTypes.EmitAction({
    actionArgs: act_4615685607825208_emit_ksk21x00v_getActionArgs,
  });
  const inst_evh_4615685607825208_updateModelValue_bm1yrfr5c =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_emit_ksk21x00v,
      event: 'update:modelValue',
      displayName: 'emit',
    });
  const inst_evh_4615685607825208_updateModelValue =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_4615685607825208_updateModelValue_tjuos7a6q,
        inst_evh_4615685607825208_updateModelValue_mq752jdse,
        inst_evh_4615685607825208_updateModelValue_bm1yrfr5c,
      ],
      event: 'update:modelValue',
    });
  const act_47936024368137_emit_sww6hcxgj = new actionTypes.EmitAction({
    actionArgs: act_47936024368137_emit_sww6hcxgj_getActionArgs,
  });
  const inst_evh_47936024368137_change_qo1umr2uo =
    new actionTypes.EventHandlerImpl({
      action: act_47936024368137_emit_sww6hcxgj,
      event: 'change',
      displayName: 'emit',
    });
  const inst_evh_47936024368137_change = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_47936024368137_change_qo1umr2uo],
    event: 'change',
  });

  const actions = {
    inst_evh_7315092382398562_reload_3dueb5g2j,
    inst_evh_7315092382398562_reload,
    inst_evh_4615685607825208_updateModelValue_tjuos7a6q,
    inst_evh_4615685607825208_updateModelValue_mq752jdse,
    inst_evh_4615685607825208_updateModelValue_bm1yrfr5c,
    inst_evh_4615685607825208_updateModelValue,
    inst_evh_47936024368137_change_qo1umr2uo,
    inst_evh_47936024368137_change,
  };

  const evh_7315092382398562_reload_3dueb5g2j = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_3dueb5g2j.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_updateModelValue_tjuos7a6q = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4615685607825208_updateModelValue_tjuos7a6q.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_updateModelValue_mq752jdse = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4615685607825208_updateModelValue_mq752jdse.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_updateModelValue_bm1yrfr5c = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4615685607825208_updateModelValue_bm1yrfr5c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_updateModelValue = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_updateModelValue.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_47936024368137_change_qo1umr2uo = async (event, scope) => {
    return await actions.inst_evh_47936024368137_change_qo1umr2uo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_47936024368137_change = async (event, scope) => {
    return await actions.inst_evh_47936024368137_change.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpRow v-if="loaded">
      <ItpCol :w="12">
        <ItpFormGroup
          :label-cols-md="2"
          :content-cols-md="5"
          name="account"
          :label="pgettext('Payment', 'Account')"
          :labelAlign="labelAlign"
          required
        >
          <UcAccountSelector
            cacheKey="Payment"
            v-model="payment.account"
            :accountType="payment.account.account_type"
            :readonly="isFieldReadonly('account')"
            class="me-2"
            @update:modelValue="
              evh_4615685607825208_updateModelValue($event, {})
            "
          >
          </UcAccountSelector>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="2"
          :content-cols-md="10"
          name="transaction_title"
          :label="pgettext('Payment', 'Title')"
          :labelAlign="labelAlign"
          v-if="isFieldEnabled('transaction_title')"
        >
          <ItpFormSearchableSelect
            name="transaction_title"
            v-model="payment.transaction_title"
            valueField="name"
            textField="name"
            :options="transactionTitles"
            taggable
            clearable
            :readonly="isFieldReadonly('transaction_title')"
          >
          </ItpFormSearchableSelect>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="2"
          :content-cols-md="10"
          name="client"
          :label="pgettext('Payment', 'Client')"
          :labelAlign="labelAlign"
          v-if="isFieldEnabled('partner')"
        >
          <ItpFormSearchableSelect
            name="client"
            v-model="payment.partner"
            bind-object
            :dataSource="b_8697745105058001_dataSource"
            optionTemplate="<UcSuggestCompanyOption :option=option></UcSuggestCompanyOption>"
            use-option-template-for-selected-option
            textField="name"
            clearable
            :readonly="isFieldReadonly('partner')"
          >
          </ItpFormSearchableSelect>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="2"
          :content-cols-md="6"
          name="client"
          :label="pgettext('Payment', 'Employee')"
          :labelAlign="labelAlign"
          v-if="isFieldEnabled('employee')"
        >
          <ItpFormSearchableSelect
            name="client"
            v-model="payment.employee"
            bind-object
            :options="employees"
            textField="name"
            clearable
            :readonly="isFieldReadonly('employee')"
          >
          </ItpFormSearchableSelect>
        </ItpFormGroup>
      </ItpCol>
    </ItpRow>
    <hr />
    <ItpRow>
      <ItpCol :w="5" form>
        <ItpFormGroup
          :label-cols-md="5"
          :content-cols-md="7"
          name="transaction_number"
          :label="pgettext('Payment', 'Number')"
          :labelAlign="labelAlign"
          :required="!isFieldReadonly('transaction_number')"
          v-if="isFieldEnabled('transaction_number')"
        >
          <ItpFormInput
            name="transaction_number"
            v-model="payment.transaction_number"
            :readonly="isFieldReadonly('transaction_number')"
            :placeholder="
              pgettext(
                'Payment|transaction_number|placeholder',
                'Enter payment number'
              )
            "
            :required="!isFieldReadonly('transaction_number')"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="5"
          :content-cols-md="7"
          name="transaction_date"
          :label="pgettext('Payment', 'Payment Date')"
          :labelAlign="labelAlign"
          required
          v-if="isFieldEnabled('transaction_date')"
        >
          <ItpFormDatetime
            name="transaction_date"
            v-model="payment.transaction_date"
            :readonly="isFieldReadonly('transaction_date')"
            required
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="5"
          :content-cols-md="7"
          name="payment_method"
          :label="pgettext('Payment', 'Method')"
          :labelAlign="labelAlign"
          required
          v-if="isFieldEnabled('payment_method')"
        >
          <ItpFormNativeSelect
            name="payment_method"
            v-model="payment.payment_method"
            :options="paymentMethods"
            :readonly="isFieldReadonly('payment_method')"
            required
          >
          </ItpFormNativeSelect>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="5"
          :content-cols-md="7"
          name="transaction_type"
          :label="pgettext('Payment', 'Payment type')"
          :labelAlign="labelAlign"
          required
          v-if="isFieldEnabled('transaction_type') && transactionTypes"
        >
          <ItpFormNativeSelect
            name="transaction_type"
            v-model="payment.transaction_type"
            :options="
              Object.entries(transactionTypes || {}).map(([k, v]) => ({
                value: k,
                text: v,
              }))
            "
            :readonly="isFieldReadonly('transaction_type')"
            required
          >
          </ItpFormNativeSelect>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="5"
          :content-cols-md="7"
          name="amount"
          :label="pgettext('Payment', 'Amount')"
          :labelAlign="labelAlign"
          :appendText="payment.currency"
          required
          v-if="isFieldEnabled('amount')"
        >
          <ItpFormInput
            autofocus
            name="amount"
            v-model="payment.amount"
            :readonly="isFieldReadonly('amount')"
            step="any"
            required
            :min="0"
            mask="number"
            unmask="typed"
            :scale="2"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="2"
          :content-cols-md="10"
          name="autocalcAmount"
          :labelAlign="labelAlign"
          v-if="isFieldEnabled('amount') && !payment.isReadonly"
        >
          <ItpFormCheckbox
            name="autocalcAmount"
            inline
            v-model="autocalcAmountInternal"
            :title="
              pgettext(
                'Payment',
                'Automatically recalculates payment when the invoice settlement changes.'
              )
            "
            v-if="!payment.isReadonly"
            @change="evh_47936024368137_change($event, {})"
          >
            <span>
              {{ pgettext('PaymentInvoices', 'Autocalc total amount.') }}
            </span>
          </ItpFormCheckbox>
        </ItpFormGroup>
      </ItpCol>
      <ItpCol :w="7">
        <ItpFormGroup
          :label-cols-md="4"
          :content-cols-md="8"
          name="description"
          :label="pgettext('Payment', 'Description')"
          :labelAlign="labelAlign"
        >
          <ItpFormInput
            name="description"
            v-model="payment.description"
            :readonly="isReadOnly"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="4"
          name="reference_number"
          :label="pgettext('Payment', 'Reference number')"
          :labelAlign="labelAlign"
        >
          <ItpFormInput
            name="reference_number"
            v-model="payment.reference_number"
            :readonly="isReadOnly"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="4"
          name="statement_number"
          :label="pgettext('Payment', 'Statement number')"
          :labelAlign="labelAlign"
          v-if="isFieldEnabled('statement_number')"
        >
          <ItpFormInput
            name="statement_number"
            v-model="payment.statement_number"
            :readonly="isReadOnly"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="4"
          :content-cols-md="4"
          name="transaction_code"
          :label="pgettext('Payment', 'Transaction code')"
          :labelAlign="labelAlign"
          v-if="isFieldEnabled('transaction_code')"
        >
          <ItpFormInput
            name="transaction_code"
            v-model="payment.transaction_code"
            :readonly="isReadOnly"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="4"
          name="file_number"
          :label="pgettext('Payment', 'File number')"
          :labelAlign="labelAlign"
        >
          <ItpFormInput
            name="file_number"
            v-model="payment.file_number"
            :readonly="isReadOnly"
          >
          </ItpFormInput>
        </ItpFormGroup>
      </ItpCol>
    </ItpRow>
    <hr />
    <ItpRow>
      <ItpCol :w="12" form>
        <ItpFormGroup
          :label-cols-md="2"
          :content-cols-md="10"
          name="notes"
          :label="pgettext('Payment', 'Message')"
          :labelAlign="labelAlign"
        >
          <ItpFormTextarea
            name="notes"
            v-model="payment.notes"
            :readonly="isReadOnly"
          >
          </ItpFormTextarea>
        </ItpFormGroup>
      </ItpCol>
    </ItpRow>
  </ItpUsercontrol>
</template>
